import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Camera,
  Edit3,
  Gamepad2,
  Image as ImageIcon,
  Layers,
  Loader2,
  Map as MapIcon,
  Plus,
  Settings,
  Trash2,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  NumberInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Switch,
  Tabs,
} from "../../../../components/ui";
import { trpc, type RouterOutputs } from "../../../../lib/trpc";
import { uploadFile } from "../../../../lib/uploadFile";

export const Route = createFileRoute("/app/admin/games/$gameId")({
  component: GameDetailPage,
});

const platformOptions = [
  { value: "", label: "(none)" },
  { value: "riot", label: "Riot" },
  { value: "steam", label: "Steam" },
  { value: "microsoft", label: "Microsoft" },
  { value: "epic", label: "Epic" },
  { value: "battlenet", label: "Battle.net" },
];

function GameDetailPage() {
  const { gameId } = Route.useParams();
  const { data: game, isLoading } = trpc.game.adminGet.useQuery({ gameId });
  const [activeTab, setActiveTab] = useState("overview");

  if (isLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto w-full space-y-6">
        <div className="h-4 w-32 bg-surface-700/50 rounded animate-pulse" />
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-lg bg-surface-700 animate-pulse" />
          <div className="h-7 w-48 bg-surface-700 rounded animate-pulse" />
        </div>
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-9 w-24 bg-surface-800 rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="rounded-xl bg-surface-800 border border-surface-700 p-6 space-y-4">
          <div className="h-6 w-32 bg-surface-700 rounded animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 bg-surface-700/50 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="p-6">
        <Breadcrumbs
          items={[{ label: "Games", to: "/app/admin/games" }, { label: "Game Not Found" }]}
        />
        <p className="text-surface-400 mt-6">Game not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto w-full space-y-6">
      <Breadcrumbs items={[{ label: "Games", to: "/app/admin/games" }, { label: game.name }]} />

      <div className="flex items-center gap-3">
        {game.iconUrl && (
          <img src={game.iconUrl} alt={game.name} className="size-10 rounded-lg object-cover" />
        )}
        <h1 className="text-2xl font-bold text-white tracking-wide">{game.name}</h1>
      </div>

      <Tabs
        tabs={[
          { id: "overview", label: "Overview", icon: <Settings /> },
          { id: "modes", label: "Modes", icon: <Gamepad2 /> },
          { id: "maps", label: "Maps", icon: <MapIcon /> },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === "overview" && <OverviewTab game={game} />}
      {activeTab === "modes" && <ModesTab gameId={gameId} />}
      {activeTab === "maps" && <MapsTab gameId={gameId} />}
    </div>
  );
}

// =============================================================================
// OVERVIEW TAB
// =============================================================================

type GameData = NonNullable<RouterOutputs["game"]["adminGet"]>;

function OverviewTab({ game }: { game: GameData }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const gameId = game.id;

  const [name, setName] = useState(game.name);
  const [slug, setSlug] = useState(game.slug);
  const [platform, setPlatform] = useState(game.platform ?? "");
  const [defaultTeamSize, setDefaultTeamSize] = useState(game.defaultTeamSize);
  const [hasRoles, setHasRoles] = useState(game.hasRoles);
  const [iconUrl, setIconUrl] = useState(game.iconUrl ?? "");
  const [isActive, setIsActive] = useState(game.isActive);
  const [isSaving, setIsSaving] = useState(false);

  // Icon upload state
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null);

  useEffect(() => {
    setName(game.name);
    setSlug(game.slug);
    setPlatform(game.platform ?? "");
    setDefaultTeamSize(game.defaultTeamSize);
    setHasRoles(game.hasRoles);
    setIconUrl(game.iconUrl ?? "");
    setIsActive(game.isActive);
  }, [game]);

  const handleIconSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Use PNG, JPEG, or WebP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large. Max 2 MB.");
      return;
    }
    if (iconPreview) URL.revokeObjectURL(iconPreview);
    setIconPreview(URL.createObjectURL(file));
    setPendingIconFile(file);
  };

  const doUpload = async (
    file: File,
    category: "game-icon" | "map-image" | "map-thumbnail",
    entityId: string,
  ): Promise<string> => {
    const { publicUrl } = await uploadFile(file, category, entityId);
    if (!publicUrl) throw new Error("Upload did not return a public URL");
    return publicUrl;
  };

  const updateMutation = trpc.game.update.useMutation({
    onSuccess: () => {
      toast.success("Game updated");
      void utils.game.adminGet.invalidate({ gameId });
      void utils.game.adminList.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const deleteMutation = trpc.game.delete.useMutation({
    onSuccess: () => {
      toast.success("Game deleted");
      void navigate({ to: "/app/admin/games" });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let resolvedIconUrl: string | null = iconUrl || null;
      if (pendingIconFile) {
        resolvedIconUrl = await doUpload(pendingIconFile, "game-icon", gameId);
      }
      await updateMutation.mutateAsync({
        id: gameId,
        name,
        slug,
        platform:
          platform === ""
            ? null
            : (platform as "riot" | "steam" | "microsoft" | "epic" | "battlenet"),
        defaultTeamSize,
        hasRoles,
        iconUrl: resolvedIconUrl,
        isActive,
      });
      setPendingIconFile(null);
      setIconPreview(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Edit form */}
      <div className="rounded-xl bg-surface-800 border border-surface-700 p-6 space-y-4">
        <h2 className="text-lg font-bold text-white">Game Settings</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-content-secondary">Platform</label>
            <Select
              options={platformOptions}
              value={platform}
              onChange={setPlatform}
              placeholder="Select platform..."
            />
          </div>
          <NumberInput
            label="Default Team Size"
            value={defaultTeamSize}
            min={1}
            max={100}
            onChange={(e) => setDefaultTeamSize(Number(e.target.value))}
          />
        </div>

        {/* Game Icon Upload */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-content-secondary">Game Icon</label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => iconInputRef.current?.click()}
              className="group relative size-20 rounded-xl border-2 border-dashed border-surface-600 hover:border-brand-500/60 bg-surface-800/60 hover:bg-surface-800 transition-all overflow-hidden shrink-0"
            >
              {iconPreview || game.iconUrl ? (
                <>
                  <img
                    src={iconPreview ?? game.iconUrl!}
                    alt="Game icon"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-surface-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="size-5 text-white" />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-1">
                  <ImageIcon className="size-5 text-surface-500 group-hover:text-brand-400 transition-colors" />
                  <span className="text-[10px] text-surface-500">Upload</span>
                </div>
              )}
            </button>
            <input
              ref={iconInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleIconSelect}
              className="hidden"
            />
            <p className="text-xs text-surface-400">
              PNG, JPEG, or WebP. Max 2 MB.
              {pendingIconFile && (
                <span className="block text-brand-400 mt-1">
                  New icon selected. Save to upload.
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-6 pt-2">
          <Switch
            label="Has Roles"
            checked={hasRoles}
            onChange={(e) => setHasRoles(e.target.checked)}
          />
          <Switch
            label="Active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </div>

        <div className="pt-2">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            leftIcon={isSaving ? <Loader2 className="animate-spin" /> : undefined}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          {
            label: "Modes",
            value: game.modeCount,
            icon: <Gamepad2 className="size-5 text-brand-500" />,
          },
          {
            label: "Maps",
            value: game.mapCount,
            icon: <MapIcon className="size-5 text-brand-500" />,
          },
          {
            label: "Clubs",
            value: game.clubCount,
            icon: <Layers className="size-5 text-brand-500" />,
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl bg-surface-800 border border-surface-700 p-4 flex items-center gap-3"
          >
            {stat.icon}
            <div>
              <p className="text-2xl font-display font-bold text-white">{stat.value}</p>
              <p className="text-sm text-surface-400">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Delete section */}
      <div className="rounded-xl bg-surface-800 border border-error-500/30 p-6 space-y-3">
        <h2 className="text-lg font-bold text-error-400">Danger Zone</h2>
        <p className="text-sm text-surface-400">
          Permanently delete this game and all associated data. This action cannot be undone.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="danger" leftIcon={<Trash2 />}>
              Delete Game
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {game.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the game, its modes, maps, and all associated data.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate({ id: gameId })}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// =============================================================================
// MODES TAB
// =============================================================================

function ModesTab({ gameId }: { gameId: string }) {
  const utils = trpc.useUtils();
  const { data: modes, isLoading } = trpc.game.listModes.useQuery({ gameId });
  const [addOpen, setAddOpen] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);

  // Add form state
  const [addName, setAddName] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [addTeamSize, setAddTeamSize] = useState<number | null>(null);
  const [addIsRanked, setAddIsRanked] = useState(true);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTeamSize, setEditTeamSize] = useState<number | null>(null);
  const [editIsRanked, setEditIsRanked] = useState(true);

  const invalidate = () => {
    void utils.game.listModes.invalidate({ gameId });
    void utils.game.adminGet.invalidate({ gameId });
    void utils.game.adminList.invalidate();
  };

  const createMutation = trpc.game.createMode.useMutation({
    onSuccess: () => {
      toast.success("Mode created");
      invalidate();
      setAddOpen(false);
      setAddName("");
      setAddDescription("");
      setAddTeamSize(null);
      setAddIsRanked(true);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.game.updateMode.useMutation({
    onSuccess: () => {
      toast.success("Mode updated");
      invalidate();
      setEditMode(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.game.deleteMode.useMutation({
    onSuccess: () => {
      toast.success("Mode deleted");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleActiveMutation = trpc.game.updateMode.useMutation({
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const openEdit = (mode: NonNullable<typeof modes>[number]) => {
    setEditName(mode.name);
    setEditDescription(mode.description ?? "");
    setEditTeamSize(mode.teamSize);
    setEditIsRanked(mode.isRanked);
    setEditMode(mode.id);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-32 bg-surface-700 rounded animate-pulse" />
          <div className="h-8 w-24 bg-surface-700/50 rounded-lg animate-pulse" />
        </div>
        <div className="rounded-xl bg-surface-800 border border-surface-700 divide-y divide-surface-700">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <div className="flex-1 space-y-1.5">
                <div className="h-4 w-32 bg-surface-700/50 rounded animate-pulse" />
                <div className="h-3 w-48 bg-surface-700/30 rounded animate-pulse" />
              </div>
              <div className="h-5 w-9 bg-surface-700/30 rounded-full animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Game Modes</h2>
        <Button size="sm" leftIcon={<Plus />} onClick={() => setAddOpen(true)}>
          Add Mode
        </Button>
      </div>

      {modes && modes.length > 0 ? (
        <div className="rounded-xl bg-surface-800 border border-surface-700 divide-y divide-surface-700">
          {modes.map((mode) => (
            <div key={mode.id} className="flex items-center gap-4 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate">{mode.name}</span>
                  <Badge color="gray" size="sm">
                    {mode.slug}
                  </Badge>
                  {mode.isRanked && (
                    <Badge color="brand" size="sm">
                      Ranked
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-surface-400 mt-0.5">
                  Team size: {mode.teamSize ?? "inherit"} &middot; {mode.mapCount} map(s)
                </p>
              </div>

              <Switch
                size="sm"
                checked={mode.isActive}
                onChange={() =>
                  toggleActiveMutation.mutate({ id: mode.id, isActive: !mode.isActive })
                }
              />

              <ModeMapPopover gameId={gameId} gameModeId={mode.id} />

              <Button size="icon-sm" variant="ghost" onClick={() => openEdit(mode)}>
                <Edit3 />
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="icon-sm" variant="ghost">
                    <Trash2 className="text-error-400" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {mode.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this game mode and its map assignments.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteMutation.mutate({ id: mode.id })}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-surface-800 border border-surface-700 p-8 text-center">
          <Gamepad2 className="size-8 text-surface-500 mx-auto mb-2" />
          <p className="text-surface-400 text-sm">No modes yet. Add your first mode.</p>
        </div>
      )}

      {/* Add Mode Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>Add Mode</DialogHeader>
          <DialogBody className="space-y-4">
            <Input label="Name" value={addName} onChange={(e) => setAddName(e.target.value)} />
            <Input
              label="Description"
              value={addDescription}
              onChange={(e) => setAddDescription(e.target.value)}
            />
            <NumberInput
              label="Team Size (optional)"
              value={addTeamSize ?? 0}
              min={0}
              max={100}
              onChange={(e) => {
                const v = Number(e.target.value);
                setAddTeamSize(v === 0 ? null : v);
              }}
            />
            <Switch
              label="Ranked"
              checked={addIsRanked}
              onChange={(e) => setAddIsRanked(e.target.checked)}
            />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              onClick={() =>
                createMutation.mutate({
                  gameId,
                  name: addName,
                  description: addDescription || null,
                  teamSize: addTeamSize,
                  isRanked: addIsRanked,
                })
              }
              disabled={!addName.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mode Dialog */}
      <Dialog open={editMode !== null} onOpenChange={(open) => !open && setEditMode(null)}>
        <DialogContent>
          <DialogHeader>Edit Mode</DialogHeader>
          <DialogBody className="space-y-4">
            <Input label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            <Input
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <NumberInput
              label="Team Size (optional)"
              value={editTeamSize ?? 0}
              min={0}
              max={100}
              onChange={(e) => {
                const v = Number(e.target.value);
                setEditTeamSize(v === 0 ? null : v);
              }}
            />
            <Switch
              label="Ranked"
              checked={editIsRanked}
              onChange={(e) => setEditIsRanked(e.target.checked)}
            />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (!editMode) return;
                updateMutation.mutate({
                  id: editMode,
                  name: editName,
                  description: editDescription || null,
                  teamSize: editTeamSize,
                  isRanked: editIsRanked,
                });
              }}
              disabled={!editName.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// MODE MAP POPOVER
// =============================================================================

function ModeMapPopover({ gameId, gameModeId }: { gameId: string; gameModeId: string }) {
  const utils = trpc.useUtils();
  const { data: allMaps } = trpc.game.listMaps.useQuery({ gameId });
  const { data: assignedMapIds } = trpc.game.getModeMaps.useQuery({ gameModeId });

  const setModeMapsMutation = trpc.game.setModeMaps.useMutation({
    onSuccess: () => {
      void utils.game.getModeMaps.invalidate({ gameModeId });
      void utils.game.listModes.invalidate({ gameId });
    },
    onError: (err) => toast.error(err.message),
  });

  const toggle = (mapId: string) => {
    if (!assignedMapIds) return;
    const next = assignedMapIds.includes(mapId)
      ? assignedMapIds.filter((id) => id !== mapId)
      : [...assignedMapIds, mapId];
    setModeMapsMutation.mutate({ gameModeId, mapIds: next });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="icon-sm" variant="ghost">
          <MapIcon className="text-surface-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <p className="text-sm font-medium text-white mb-3">Assign Maps</p>
        {allMaps && allMaps.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {allMaps.map((map) => (
              <Checkbox
                key={map.id}
                label={map.name}
                checked={assignedMapIds?.includes(map.id) ?? false}
                onChange={() => toggle(map.id)}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-surface-400">No maps available for this game.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// MAPS TAB
// =============================================================================

function MapsTab({ gameId }: { gameId: string }) {
  const utils = trpc.useUtils();
  const { data: mapsList, isLoading } = trpc.game.listMaps.useQuery({ gameId });
  const [addOpen, setAddOpen] = useState(false);
  const [editMap, setEditMap] = useState<string | null>(null);

  // Add form state
  const [addName, setAddName] = useState("");
  const [addImageFile, setAddImageFile] = useState<File | null>(null);
  const [addImagePreview, setAddImagePreview] = useState<string | null>(null);
  const [addThumbFile, setAddThumbFile] = useState<File | null>(null);
  const [addThumbPreview, setAddThumbPreview] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const addImageRef = useRef<HTMLInputElement>(null);
  const addThumbRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editThumbFile, setEditThumbFile] = useState<File | null>(null);
  const [editThumbPreview, setEditThumbPreview] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const editImageRef = useRef<HTMLInputElement>(null);
  const editThumbRef = useRef<HTMLInputElement>(null);

  const invalidate = () => {
    void utils.game.listMaps.invalidate({ gameId });
    void utils.game.adminGet.invalidate({ gameId });
    void utils.game.adminList.invalidate();
  };

  const doMapUpload = async (
    file: File,
    category: "map-image" | "map-thumbnail",
    entityId: string,
  ): Promise<string> => {
    const { publicUrl } = await uploadFile(file, category, entityId);
    if (!publicUrl) throw new Error("Upload did not return a public URL");
    return publicUrl;
  };

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (f: File | null) => void,
    setPreview: (url: string | null) => void,
    currentPreview: string | null,
    maxSize: number,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Use PNG, JPEG, or WebP.");
      return;
    }
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${maxSize / (1024 * 1024)} MB.`);
      return;
    }
    if (currentPreview) URL.revokeObjectURL(currentPreview);
    setPreview(URL.createObjectURL(file));
    setFile(file);
  };

  const createMutation = trpc.game.createMap.useMutation({
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.game.updateMap.useMutation({
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.game.deleteMap.useMutation({
    onSuccess: () => {
      toast.success("Map deleted");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleActiveMutation = trpc.game.updateMap.useMutation({
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      // Create map first to get the ID for uploads
      const created = await createMutation.mutateAsync({ gameId, name: addName });
      if (created && (addImageFile || addThumbFile)) {
        const updates: { id: string; imageUrl?: string; thumbnailUrl?: string } = {
          id: created.id,
        };
        if (addImageFile) {
          updates.imageUrl = await doMapUpload(addImageFile, "map-image", created.id);
        }
        if (addThumbFile) {
          updates.thumbnailUrl = await doMapUpload(addThumbFile, "map-thumbnail", created.id);
        }
        await updateMutation.mutateAsync(updates);
      }
      toast.success("Map created");
      invalidate();
      resetAddForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create map");
    } finally {
      setIsCreating(false);
    }
  };

  const resetAddForm = () => {
    setAddOpen(false);
    setAddName("");
    setAddImageFile(null);
    setAddThumbFile(null);
    if (addImagePreview) URL.revokeObjectURL(addImagePreview);
    if (addThumbPreview) URL.revokeObjectURL(addThumbPreview);
    setAddImagePreview(null);
    setAddThumbPreview(null);
  };

  const handleUpdate = async () => {
    if (!editMap) return;
    setIsUpdating(true);
    try {
      const updates: { id: string; name: string; imageUrl?: string; thumbnailUrl?: string } = {
        id: editMap,
        name: editName,
      };
      if (editImageFile) {
        updates.imageUrl = await doMapUpload(editImageFile, "map-image", editMap);
      }
      if (editThumbFile) {
        updates.thumbnailUrl = await doMapUpload(editThumbFile, "map-thumbnail", editMap);
      }
      await updateMutation.mutateAsync(updates);
      toast.success("Map updated");
      invalidate();
      resetEditForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update map");
    } finally {
      setIsUpdating(false);
    }
  };

  const resetEditForm = () => {
    setEditMap(null);
    setEditName("");
    setEditImageFile(null);
    setEditThumbFile(null);
    if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    if (editThumbPreview) URL.revokeObjectURL(editThumbPreview);
    setEditImagePreview(null);
    setEditThumbPreview(null);
  };

  const openEdit = (map: NonNullable<typeof mapsList>[number]) => {
    setEditName(map.name);
    setEditMap(map.id);
    setEditImageFile(null);
    setEditThumbFile(null);
    setEditImagePreview(null);
    setEditThumbPreview(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-20 bg-surface-700 rounded animate-pulse" />
          <div className="h-8 w-24 bg-surface-700/50 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-xl bg-surface-800 border border-surface-700 overflow-hidden"
            >
              <div className="w-full h-32 bg-surface-700 animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-4 w-24 bg-surface-700/50 rounded animate-pulse" />
                <div className="h-3 w-16 bg-surface-700/30 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Maps</h2>
        <Button size="sm" leftIcon={<Plus />} onClick={() => setAddOpen(true)}>
          Add Map
        </Button>
      </div>

      {mapsList && mapsList.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {mapsList.map((map) => (
            <div
              key={map.id}
              className="rounded-xl bg-surface-800 border border-surface-700 overflow-hidden"
            >
              {map.thumbnailUrl ? (
                <img src={map.thumbnailUrl} alt={map.name} className="w-full h-32 object-cover" />
              ) : (
                <div className="w-full h-32 bg-surface-700 flex items-center justify-center">
                  <MapIcon className="size-8 text-surface-500" />
                </div>
              )}
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">{map.name}</p>
                    <p className="text-xs text-surface-400">{map.slug}</p>
                  </div>
                  <Switch
                    size="sm"
                    checked={map.isActive}
                    onChange={() =>
                      toggleActiveMutation.mutate({ id: map.id, isActive: !map.isActive })
                    }
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon-sm" variant="ghost" onClick={() => openEdit(map)}>
                    <Edit3 />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon-sm" variant="ghost">
                        <Trash2 className="text-error-400" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {map.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete this map. Mode-map assignments referencing it
                          must be removed first.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteMutation.mutate({ id: map.id })}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-surface-800 border border-surface-700 p-8 text-center">
          <MapIcon className="size-8 text-surface-500 mx-auto mb-2" />
          <p className="text-surface-400 text-sm">No maps yet. Add your first map.</p>
        </div>
      )}

      {/* Add Map Dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (!open) resetAddForm();
        }}
      >
        <DialogContent>
          <DialogHeader>Add Map</DialogHeader>
          <DialogBody className="space-y-4">
            <Input label="Name" value={addName} onChange={(e) => setAddName(e.target.value)} />
            <MapImageField
              label="Map Image"
              preview={addImagePreview}
              inputRef={addImageRef}
              onSelect={(e) =>
                handleFileSelect(
                  e,
                  setAddImageFile,
                  setAddImagePreview,
                  addImagePreview,
                  5 * 1024 * 1024,
                )
              }
              hint="Full-size map image. Max 5 MB."
            />
            <MapImageField
              label="Thumbnail"
              preview={addThumbPreview}
              inputRef={addThumbRef}
              onSelect={(e) =>
                handleFileSelect(
                  e,
                  setAddThumbFile,
                  setAddThumbPreview,
                  addThumbPreview,
                  2 * 1024 * 1024,
                )
              }
              hint="Small preview image. Max 2 MB."
            />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleCreate}
              disabled={!addName.trim() || isCreating}
              leftIcon={isCreating ? <Loader2 className="animate-spin" /> : undefined}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Map Dialog */}
      <Dialog
        open={editMap !== null}
        onOpenChange={(open) => {
          if (!open) resetEditForm();
        }}
      >
        <DialogContent>
          <DialogHeader>Edit Map</DialogHeader>
          <DialogBody className="space-y-4">
            <Input label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            <MapImageField
              label="Map Image"
              preview={editImagePreview}
              existingUrl={mapsList?.find((m) => m.id === editMap)?.imageUrl}
              inputRef={editImageRef}
              onSelect={(e) =>
                handleFileSelect(
                  e,
                  setEditImageFile,
                  setEditImagePreview,
                  editImagePreview,
                  5 * 1024 * 1024,
                )
              }
              hint="Full-size map image. Max 5 MB."
            />
            <MapImageField
              label="Thumbnail"
              preview={editThumbPreview}
              existingUrl={mapsList?.find((m) => m.id === editMap)?.thumbnailUrl}
              inputRef={editThumbRef}
              onSelect={(e) =>
                handleFileSelect(
                  e,
                  setEditThumbFile,
                  setEditThumbPreview,
                  editThumbPreview,
                  2 * 1024 * 1024,
                )
              }
              hint="Small preview image. Max 2 MB."
            />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleUpdate}
              disabled={!editName.trim() || isUpdating}
              leftIcon={isUpdating ? <Loader2 className="animate-spin" /> : undefined}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// MAP IMAGE FIELD (shared upload widget for map dialogs)
// =============================================================================

function MapImageField({
  label,
  preview,
  existingUrl,
  inputRef,
  onSelect,
  hint,
}: {
  label: string;
  preview: string | null;
  existingUrl?: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hint: string;
}) {
  const displayUrl = preview || existingUrl;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-content-secondary">{label}</label>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="group relative w-full h-28 rounded-lg border-2 border-dashed border-surface-600 hover:border-brand-500/60 bg-surface-800/60 hover:bg-surface-800 transition-all overflow-hidden"
      >
        {displayUrl ? (
          <>
            <img src={displayUrl} alt={label} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-surface-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="size-5 text-white" />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <Upload className="size-5 text-surface-500 group-hover:text-brand-400 transition-colors" />
            <span className="text-xs text-surface-500">Click to upload</span>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onSelect}
        className="hidden"
      />
      <p className="text-xs text-surface-400">{hint}</p>
    </div>
  );
}
